const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseDateKey = (value) => {
  if (!value) return null;
  const datePart = String(value).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

export const addDays = (date, count) => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + count);
  return next;
};

export const daysFromToday = (dateKey, todayKey) => {
  const date = parseDateKey(dateKey);
  const today = parseDateKey(todayKey);
  if (!date || !today) return 0;
  return Math.round((date.getTime() - today.getTime()) / MS_PER_DAY);
};

export const getWeatherEndpointForDate = (dateKey, todayKey) => {
  const delta = daysFromToday(dateKey, todayKey);
  if (delta < 0) return 'history';
  if (delta > 14) return 'future';
  return 'forecast';
};

/**
 * Up to 3 consecutive days that belong to the trip.
 * Upcoming trips use the first 3 days. In-progress trips start from today.
 * Past trips use the first 3 trip days so historical weather still lines up.
 */
export const getTripWeatherDates = (tripDates, today = new Date()) => {
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const start = parseDateKey(tripDates?.start);
  const end = parseDateKey(tripDates?.end) || start;

  if (!start) {
    return [0, 1, 2].map((offset) => toDateKey(addDays(todayStart, offset)));
  }

  const last = end && end >= start ? end : start;
  let windowStart = start;
  if (start <= todayStart && last >= todayStart) {
    windowStart = todayStart;
  }

  const dates = [];
  let cursor = windowStart;
  while (dates.length < 3 && cursor <= last) {
    dates.push(toDateKey(cursor));
    cursor = addDays(cursor, 1);
  }
  return dates;
};

export const mapForecastDay = (day) => ({
  date: day.date,
  temp: {
    min: Math.round(day.day.mintemp_c),
    max: Math.round(day.day.maxtemp_c),
    avg: Math.round(day.day.avgtemp_c),
  },
  condition: day.day.condition.text,
  description: day.day.condition.text,
  humidity: day.day.avghumidity,
  windSpeed: Math.round(day.day.maxwind_kph / 3.6),
  icon: day.day.condition.icon,
});

const weatherUrl = (endpoint, apiKey, location, extra) => {
  const params = new URLSearchParams({
    key: apiKey,
    q: location,
    aqi: 'no',
    alerts: 'no',
    ...extra,
  });
  return `https://api.weatherapi.com/v1/${endpoint}.json?${params.toString()}`;
};

const readForecastDay = async (response) => {
  if (!response.ok) return null;
  const payload = await response.json();
  const day = payload?.forecast?.forecastday?.[0];
  return day ? mapForecastDay(day) : null;
};

export const fetchWeatherForDates = async (location, dateKeys, apiKey, today = new Date()) => {
  const todayKey = toDateKey(today);
  const uniqueDates = [...new Set(dateKeys.filter(Boolean))];
  const byDate = {};

  const forecastDates = uniqueDates.filter((date) => getWeatherEndpointForDate(date, todayKey) === 'forecast');
  const historyDates = uniqueDates.filter((date) => getWeatherEndpointForDate(date, todayKey) === 'history');
  const futureDates = uniqueDates.filter((date) => getWeatherEndpointForDate(date, todayKey) === 'future');

  if (forecastDates.length > 0) {
    const maxDelta = Math.max(...forecastDates.map((date) => daysFromToday(date, todayKey)));
    const days = Math.min(14, Math.max(1, maxDelta + 1));
    const bulkResponse = await fetch(weatherUrl('forecast', apiKey, location, { days: String(days) }));
    if (bulkResponse.ok) {
      const payload = await bulkResponse.json();
      (payload?.forecast?.forecastday || []).forEach((day) => {
        if (forecastDates.includes(day.date)) {
          byDate[day.date] = mapForecastDay(day);
        }
      });
    }

    await Promise.all(forecastDates.filter((date) => !byDate[date]).map(async (date) => {
      const response = await fetch(weatherUrl('forecast', apiKey, location, { dt: date, days: '1' }));
      const mapped = await readForecastDay(response);
      if (mapped) byDate[date] = mapped;
    }));
  }

  await Promise.all([...historyDates, ...futureDates].map(async (date) => {
    const endpoint = getWeatherEndpointForDate(date, todayKey);
    const response = await fetch(weatherUrl(endpoint, apiKey, location, { dt: date }));
    const mapped = await readForecastDay(response);
    if (mapped) byDate[date] = mapped;
  }));

  return uniqueDates.map((date) => byDate[date]).filter(Boolean);
};
